import { STORE_RELATIONS_FALLBACK_TEXT } from '../../constants/storeConstants'
import { resolveStoreRelationAssignmentStatus } from '../../helpers/storeRelationAssignmentUtils'
import {
  buildStoreRelationSalesmanOption,
  getStoreRelationRelationSalesmanKey
} from '../../utils/storeRelationDisplayUtils'
import {
  buildStoreRelationBrandOptions,
  buildStoreRelationEntityLabel,
  buildStoreRelationReadOnlyFilterOption
} from '../utils/storeRelationFormOptionUtils'

export const buildStoreRelationEmptyFormState = () => ({
  supplierOption: null,
  supplierSearch: '',
  buyerOption: null,
  buyerSearch: '',
  referenceOption: null,
  referenceSearch: '',
  clientOption: null,
  clientSearch: '',
  principalUserOption: null,
  principalUserSearch: '',
  shouldAssignUsersByBrand: false,
  assignmentUserSearch: '',
  assignmentRows: []
})

export const buildStoreRelationResetClientDependentState = () => ({
  clientOption: null,
  clientSearch: '',
  principalUserOption: null,
  principalUserSearch: '',
  shouldAssignUsersByBrand: false,
  assignmentUserSearch: '',
  assignmentRows: []
})

export const hasStoreRelationPayload = (payload) => {
  return Boolean(
    payload && (
      (payload.client?.id || payload.client?.db_ref) &&
      (
        payload.client_configuration?.id ||
        payload.client_configuration?.store_reference ||
        payload.client_principal?.id ||
        payload.client_principal?.store_reference
      ) &&
      Array.isArray(payload.client_brands)
    )
  )
}

export const buildStoreRelationNewAssignmentRow = (option, overrides = {}) => ({
  id: option?.id || option?.db_ref || option?.name,
  userLabel: option?.label || option?.name || STORE_RELATIONS_FALLBACK_TEXT,
  salesmanDbRef: option?.db_ref || '',
  brandOptions: [],
  brandSearch: '',
  status: 'active',
  allBrandsInError: false,
  requiresReassignment: false,
  ...overrides
})

export const buildStoreRelationAssignmentRows = (storeRelation) => {
  if (!storeRelation) return []

  const groupedRows = new Map()

  ;(storeRelation?.client_brands || []).forEach((relation, index) => {
    const salesmanKey = getStoreRelationRelationSalesmanKey(relation, `salesman-${index}`)
    const currentRow = groupedRows.get(salesmanKey) || {
      id: salesmanKey,
      userLabel: buildStoreRelationEntityLabel(relation?.salesman),
      salesmanDbRef: relation?.salesman?.db_ref || '',
      brandOptions: [],
      brandSearch: '',
      status: relation?.status || 'active',
      relations: []
    }

    buildStoreRelationBrandOptions(relation).forEach((brandOption) => {
      const alreadyExists = currentRow.brandOptions.some((item) => item.id === brandOption.id)

      if (!alreadyExists) {
        currentRow.brandOptions.push(brandOption)
      }
    })

    currentRow.relations.push(relation)
    groupedRows.set(salesmanKey, currentRow)
  })

  return Array.from(groupedRows.values())
    .map((row) => {
      const assignmentStatus = resolveStoreRelationAssignmentStatus(row.relations)

      return {
        id: row.id,
        userLabel: row.userLabel,
        salesmanDbRef: row.salesmanDbRef,
        brandOptions: row.brandOptions,
        brandSearch: row.brandSearch,
        status: assignmentStatus.hasAnyError
          ? 'error'
          : assignmentStatus.hasAnySuspended
            ? 'suspended'
            : row.status || 'active',
        allBrandsInError: assignmentStatus.allBrandsInError,
        requiresReassignment: assignmentStatus.requiresReassignment
      }
    })
    .filter((row) => row.userLabel || row.brandOptions.length > 0)
}

export const buildStoreRelationInitialFormState = (storeRelation) => {
  if (!storeRelation) return buildStoreRelationEmptyFormState()

  const assignmentRows = buildStoreRelationAssignmentRows(storeRelation)
  const canSplitOrderByBrand = Boolean(storeRelation?.flags?.split_order_by_brand)
  const principalSalesman = storeRelation?.client_principal?.salesman || null
  const clientLabel = buildStoreRelationEntityLabel(storeRelation?.client)
  const principalUserOption = principalSalesman
    ? buildStoreRelationSalesmanOption(principalSalesman)
    : null

  return {
    supplierOption: {
      id: storeRelation?.client_configuration?.supplier_id || 'supplier',
      label:
        storeRelation?.client_configuration?.supplier_name ||
        storeRelation?.client_principal?.supplier_name ||
        '',
      value: storeRelation?.client_configuration?.supplier_id || null,
      raw: null
    },
    supplierSearch:
      storeRelation?.client_configuration?.supplier_name ||
      storeRelation?.client_principal?.supplier_name ||
      '',
    buyerOption: {
      id: storeRelation?.client_configuration?.buyer_id || 'buyer',
      label:
        storeRelation?.client_configuration?.buyer_name ||
        storeRelation?.client_principal?.buyer_name ||
        '',
      value: storeRelation?.client_configuration?.buyer_id || null,
      raw: null
    },
    buyerSearch:
      storeRelation?.client_configuration?.buyer_name ||
      storeRelation?.client_principal?.buyer_name ||
      '',
    referenceOption: {
      id: storeRelation?.client_configuration?.store_reference || 'reference',
      label:
        storeRelation?.client_configuration?.store_reference ||
        storeRelation?.client_principal?.store_reference ||
        '',
      value:
        storeRelation?.client_configuration?.store_reference ||
        storeRelation?.client_principal?.store_reference ||
        '',
      raw: null
    },
    referenceSearch:
      storeRelation?.client_configuration?.store_reference ||
      storeRelation?.client_principal?.store_reference ||
      '',
    clientOption: {
      id: storeRelation?.client?.id || storeRelation?.client?.db_ref,
      db_ref: storeRelation?.client?.db_ref || '',
      name: storeRelation?.client?.name || '',
      label: clientLabel,
      raw: storeRelation?.client
        ? {
          ...storeRelation.client,
          flags: storeRelation?.flags || null
        }
        : null
    },
    clientSearch: clientLabel,
    principalUserOption,
    principalUserSearch: principalUserOption?.label || '',
    shouldAssignUsersByBrand: canSplitOrderByBrand && assignmentRows.length > 0,
    assignmentUserSearch: '',
    assignmentRows
  }
}

export const buildStoreRelationBrandValidationRows = (assignmentRows = []) => {
  return assignmentRows
    .map((row) => ({
      salesmanDbRef: row?.salesmanDbRef || '',
      userLabel: row?.userLabel || STORE_RELATIONS_FALLBACK_TEXT,
      brandDbRefs: (row?.brandOptions || [])
        .map((brand) => brand?.db_ref)
        .filter(Boolean)
    }))
    .filter((row) => row.salesmanDbRef && row.brandDbRefs.length > 0)
}

export const buildStoreRelationEditBaseInfoOptions = ({
  formState,
  effectiveStoreRelationData
}) => ({
  supplier: buildStoreRelationReadOnlyFilterOption(
    'supplier',
    formState?.supplierOption?.label ||
      effectiveStoreRelationData?.client_configuration?.supplier_name ||
      effectiveStoreRelationData?.client_principal?.supplier_name
  ),
  buyer: buildStoreRelationReadOnlyFilterOption(
    'buyer',
    formState?.buyerOption?.label ||
      effectiveStoreRelationData?.client_configuration?.buyer_name ||
      effectiveStoreRelationData?.client_principal?.buyer_name
  ),
  reference: buildStoreRelationReadOnlyFilterOption(
    'reference',
    formState?.referenceOption?.label ||
      effectiveStoreRelationData?.client_configuration?.store_reference ||
      effectiveStoreRelationData?.client_principal?.store_reference
  ),
  client: buildStoreRelationReadOnlyFilterOption(
    'client',
    formState?.clientOption?.label ||
      buildStoreRelationEntityLabel(effectiveStoreRelationData?.client)
  )
})

export const buildStoreRelationSelectedBrandDbRefsByRow = (assignmentRows = []) => {
  return assignmentRows.reduce((accumulator, row) => {
    accumulator[row.id] = (row.brandOptions || [])
      .map((brand) => brand?.db_ref)
      .filter(Boolean)

    return accumulator
  }, {})
}

export const buildStoreRelationInitialAssignedBrandDbRefs = (assignmentRows = []) => {
  return assignmentRows.flatMap((row) => (
    (row.brandOptions || [])
      .map((brand) => brand?.db_ref)
      .filter(Boolean)
  ))
}

export const buildStoreRelationInitialBrandOptionsByDbRef = (
  assignmentRows = [],
  unavailableBrandDbRefs = []
) => {
  return assignmentRows.reduce((accumulator, row) => {
    ;(row.brandOptions || []).forEach((brand) => {
      if (!brand?.db_ref || accumulator[brand.db_ref] || unavailableBrandDbRefs.includes(brand.db_ref)) {
        return
      }

      accumulator[brand.db_ref] = brand
    })

    return accumulator
  }, {})
}
